import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import StatusBadge from '../components/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Edit, 
  Trash2, 
  Copy, 
  CheckCircle,
  Loader2,
  Map,
  Filter,
  Camera,
  FileText,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const IMPORT_TEMPLATE = `PROCESS NAME:
[Name of the process]

START:
Start name: [e.g. Job received]
Owner / swimlane: [e.g. System / Intake]
Output / next step: [first step name]

STEP 1:
Step name: [verb-led, short]
Owner / swimlane: [team or person]
System(s) used: [comma-separated systems / lists / tools]
Document(s) / artefact(s) used: [comma-separated documents / files]
Action: [what happens in this step]
Decision point: [the question the step has to answer, e.g. Is the job visible?]
Yes path: [next step name]
No path / exception path: [terminal exception, e.g. Create exception: visibility mismatch]
Output / next step: [next step name on the Yes path]

STEP 2:
Step name:
Owner / swimlane:
System(s) used:
Document(s) / artefact(s) used:
Action:
Decision point:
Yes path:
No path / exception path:
Output / next step:

(repeat the STEP block for each step in the process)

END:
End name: [e.g. Job ready for production setup]
Owner / swimlane: [final team]

---
INSTRUCTIONS FOR AI:
Rewrite my process below into the structure above.
Keep step names verb-led and short (≤6 words).
Keep decision-point questions short (≤6 words, ending in "?").
Yes path should continue the main flow; No path is the exception.
Do not invent steps that aren't in my narrative.

MY PROCESS:
[paste your raw description here]
`;

const ProcessMaps = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isApprover } = useAuth();
  const [processMaps, setProcessMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importMode, setImportMode] = useState('photo');
  const [importText, setImportText] = useState('');
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);
  const [useSwimlanes, setUseSwimlanes] = useState(null); // null = AI decides, true = force on, false = off
  const [showCallouts, setShowCallouts] = useState(true);  // waste + improvement call-outs

  useEffect(() => {
    fetchProcessMaps();
  }, [statusFilter]);

  const fetchProcessMaps = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/process-maps?${params.toString()}`);
      setProcessMaps(response.data);
    } catch (error) {
      console.error('Failed to fetch process maps:', error);
      toast.error('Failed to load process maps');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await api.delete(`/process-maps/${id}`);
      toast.success('Process map deleted');
      fetchProcessMaps();
    } catch (error) {
      console.error('Failed to delete process map:', error);
      toast.error('Failed to delete process map');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await api.post(`/process-maps/${id}/duplicate`);
      toast.success('Process map duplicated');
      navigate(`/process-maps/${response.data.id}/edit`);
    } catch (error) {
      console.error('Failed to duplicate process map:', error);
      toast.error('Failed to duplicate process map');
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/process-maps/${id}/publish`);
      toast.success('Process map published');
      fetchProcessMaps();
    } catch (error) {
      console.error('Failed to publish process map:', error);
      toast.error('Failed to publish process map');
    }
  };

  const filteredMaps = processMaps.filter(map => 
    map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    map.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    map.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Draft': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'Archived': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const handlePhotoImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', importName || 'Process Map from Photo');
      const res = await api.post(`/process-maps/from-image?name=${encodeURIComponent(importName || 'Process Map from Photo')}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success('Process map created from photo!');
      setShowImportDialog(false);
      navigate(`/process-maps/${res.data.id}/edit`);
    } catch (err) {
      toast.error('Failed to process photo');
    } finally {
      setImporting(false);
    }
  };

  const handleTextImport = async () => {
    if (!importText.trim()) { toast.error('Enter process steps'); return; }
    setImporting(true);
    try {
      const res = await api.post('/process-maps/from-text', {
        text: importText,
        name: importName || 'Process Map from Text',
        use_swimlanes: useSwimlanes,
        show_improvements: showCallouts,
        show_waste: showCallouts,
      }, { timeout: 120000 });
      toast.success('Process map created!');
      setShowImportDialog(false);
      navigate(`/process-maps/${res.data.id}/edit`);
    } catch (err) {
      toast.error('Failed to generate process map');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="process-maps-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Process Maps
          </h1>
          <p className="text-slate-500 mt-1">
            Visual process documentation with BPMN 2.0
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => { setShowImportDialog(true); setImportMode('photo'); setImportName(''); setImportText(''); }}
            data-testid="import-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Import from Photo / Text
          </Button>
          <Button 
            onClick={() => navigate('/process-maps/new')}
            className="bg-[#235894] hover:bg-[#1E4B80]"
            data-testid="create-process-map-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Process Map
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search process maps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter || 'All Status'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('')}>
                  All Status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Draft')}>
                  Draft
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Published')}>
                  Published
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Archived')}>
                  Archived
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Process Maps Table */}
      <Card>
        <CardContent className="p-0">
          {filteredMaps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Map className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {processMaps.length === 0 ? 'No process maps yet' : 'No matching process maps'}
              </h3>
              <p className="text-slate-500 text-center max-w-md mb-4">
                {processMaps.length === 0 
                  ? 'Create your first process map to document and visualize your business processes.'
                  : 'Try adjusting your search or filters to find what you\'re looking for.'}
              </p>
              {processMaps.length === 0 && (
                <Button 
                  onClick={() => navigate('/process-maps/new')}
                  className="bg-[#235894] hover:bg-[#1E4B80]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Process Map
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaps.map((map) => (
                  <TableRow 
                    key={map.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/process-maps/${map.id}`)}
                    data-testid={`process-map-row-${map.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{map.name}</p>
                        {map.description && (
                          <p className="text-sm text-slate-500 truncate max-w-xs">
                            {map.description}
                          </p>
                        )}
                        {map.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {map.tags.slice(0, 3).map((tag, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {map.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{map.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{map.department || '-'}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(map.status)}>
                        {map.status}
                      </Badge>
                    </TableCell>
                    <TableCell>v{map.version}</TableCell>
                    <TableCell>{map.created_by_name}</TableCell>
                    <TableCell>
                      {new Date(map.updated_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/process-maps/${map.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/process-maps/${map.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(map.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {(isApprover() || isAdmin()) && map.status === 'Draft' && (
                            <DropdownMenuItem onClick={() => handlePublish(map.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Publish
                            </DropdownMenuItem>
                          )}
                          {(isAdmin() || map.created_by === user?.id) && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(map.id, map.name)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Process Map</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Process Map Name</Label>
              <Input value={importName} onChange={e => setImportName(e.target.value)} placeholder="e.g., Customer Order Process" />
            </div>

            {/* Mode toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <button onClick={() => setImportMode('photo')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium ${importMode === 'photo' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500'}`}>
                <Camera className="w-4 h-4" /> Upload Photo
              </button>
              <button onClick={() => setImportMode('text')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium ${importMode === 'text' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500'}`}>
                <FileText className="w-4 h-4" /> Paste Text
              </button>
            </div>

            {importMode === 'photo' && (
              <div>
                <Label>Upload a photo of sticky notes, whiteboard, or hand-drawn process</Label>
                <div className="mt-2 border-2 border-dashed rounded-lg p-6 text-center hover:border-[#235894]/50 transition-colors">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500 mb-2">Click or drag a photo here</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handlePhotoImport(e.target.files[0])}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    style={{ position: 'relative' }}
                    data-testid="photo-upload-input"
                  />
                  <p className="text-xs text-slate-400">JPG, PNG, WebP supported</p>
                </div>
                {importing && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-[#235894]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reading sticky notes and generating BPMN...
                  </div>
                )}
              </div>
            )}

            {importMode === 'text' && (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Describe the process using the structured template</Label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        data-testid="copy-template-btn"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(IMPORT_TEMPLATE);
                            toast.success('Template copied — paste it into ChatGPT / Claude and ask it to reformat your process');
                          } catch (err) {
                            // Fallback for browsers that block clipboard API
                            const ta = document.createElement('textarea');
                            ta.value = IMPORT_TEMPLATE;
                            document.body.appendChild(ta);
                            ta.select();
                            try { document.execCommand('copy'); toast.success('Template copied'); }
                            catch { toast.error('Could not copy — please select and copy manually'); }
                            document.body.removeChild(ta);
                          }
                        }}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        Copy template
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        data-testid="paste-template-btn"
                        onClick={() => {
                          setImportText(IMPORT_TEMPLATE);
                          toast.message('Template inserted — fill in each step and re-paste over this');
                        }}
                      >
                        Insert into editor
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-1.5">
                    Tip: click <span className="font-medium text-slate-700">Copy template</span>, paste it into your favourite AI (ChatGPT, Claude, Gemini), tell it to rewrite your process into that format, then paste the result back here.
                  </p>
                  <Textarea
                    value={importText}
                    onChange={e => setImportText(e.target.value)}
                    placeholder={`PROCESS NAME:
Production Intake and Job Preparation

START:
Start name: Job received
Owner / swimlane: System / Intake
Output / next step: Job visibility check

STEP 1:
Step name: Job visibility check
Owner / swimlane: System / Intake
System(s) used: Calendar, production tracking list
Document(s) / artefact(s) used: Job pack
Action: Check whether the job is visible in the required planning sources
Decision point: Is the job visible on both the calendar and the production tracking list?
Yes path: Begin production planning
No path / exception path: Create exception: visibility mismatch
Output / next step: Daily production review

STEP 2:
Step name: Daily production review
Owner / swimlane: Planner / Jake
System(s) used: Calendar, production tracking list
Document(s) / artefact(s) used: Priority list
Action: Review the calendar and production tracking list to identify imminent jobs
Decision point: Is the job imminent?
Yes path: Prioritise job for production
No path / exception path: Hold job in queue
Output / next step: Review job pack

... (repeat the STEP block for each step) ...

END:
End name: Job ready for production setup
Owner / swimlane: Production Team`}
                    rows={16}
                    className="mt-1 font-mono text-xs"
                    data-testid="text-import-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Use the template above. Systems become data stores (light blue), documents become data objects (light grey). Decision gateways always route "Yes" right and "No" down.
                  </p>
                </div>

                <div>
                  <Label className="text-sm">Swimlanes</Label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { v: null, label: 'Auto' },
                      { v: true, label: 'Force on' },
                      { v: false, label: 'Off' },
                    ].map(opt => (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => setUseSwimlanes(opt.v)}
                        className={`text-xs px-3 py-1.5 rounded-md border ${useSwimlanes === opt.v ? 'bg-[#235894] text-white border-[#235894]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                        data-testid={`swimlane-option-${opt.label.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Auto = AI uses swimlanes only if the narrative makes responsibilities clear.
                  </p>
                </div>

                {importing && (
                  <div className="flex items-center gap-2 text-sm text-[#235894]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Extracting process graph and laying out BPMN...
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            {importMode === 'text' && (
              <Button onClick={handleTextImport} disabled={importing || !importText.trim()} className="bg-[#235894] hover:bg-[#1E4B80]">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Map className="w-4 h-4 mr-2" />}
                Generate BPMN
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessMaps;
